/*
 * lsg-bundle-pi.js — adapter that lets the existing lsg-bundle.js engine drive the bundle when it's
 * composed of theme blocks inside product-information's _product-details (instead of the old
 * lsg-bundle-builder section with 3 toggled .lsg-bundle-block).
 *
 * It synthesizes the structure the engine expects: marks the buybox container (the .product-details
 * that holds the tier-chooser) as the single .lsg-bundle-wrapper + .lsg-bundle-block-active, and on
 * tier select copies the active tier button's data-* onto that block, then re-runs the engine.
 * Reuses the engine's globals (initializeBundle / updateBundlePrice / syncSharedPickerMax).
 */
(function () {
  var TIER_KEYS = [
    "bundleProduct",
    "productBasePrice",
    "productCompareAtPrice",
    "otpBundleMin",
    "otpBundleMax",
    "subBundleMin",
    "subBundleMax",
    "tierDiscountCents",
  ];

  function copyTierData(block, btn) {
    TIER_KEYS.forEach(function (k) {
      var v = btn.dataset[k];
      if (v != null && v !== "") {
        block.dataset[k] = v;
      } else {
        delete block.dataset[k];
      }
    });
  }

  // Each tier product has its OWN Stay AI selling plans. The frequency-select is rendered from one
  // source product, so its option values are that product's plan ids. Override the form's
  // selling_plan with the ACTIVE tier's plan at the chosen frequency index (same order across tiers).
  function reconcileSellingPlan(block, activeBtn) {
    if (!block || !activeBtn) return;
    var form = block.querySelector(".lsg-bundle-form");
    if (!form) return;
    var spInput = form.querySelector('input[name="selling_plan"]');
    if (!spInput) return;
    var subChecked = block.querySelector(
      '.lsg-bundle-interval-select input[value="sub"]:checked',
    );
    if (!subChecked) return; // one-time — leave the engine's (empty) value
    var plans = (activeBtn.dataset.sellingPlans || "")
      .split(",")
      .filter(Boolean);
    if (!plans.length) return;
    var freq = block.querySelector(
      ".lsg-bundle-interval-frequency-select:not(.is-duplicate)",
    );
    var idx = freq && freq.selectedIndex >= 0 ? freq.selectedIndex : 0;
    if (idx >= plans.length) idx = 0;
    spInput.value = plans[idx];
  }

  // Show only the active tier's number of selected-summary slots (the summary renders the largest
  // tier's count; hide the surplus). Each slot fades + scales, AND the container's height eases
  // between the old and new row count so the content below (ATC, etc.) glides instead of jumping
  // when a whole row of slots appears/disappears.
  function syncSummarySlots(block, btn, instant) {
    if (!btn) return;
    var max = parseInt(btn.dataset.subBundleMax || btn.dataset.otpBundleMax, 10);
    if (!max || max < 1) return;
    var container = block.querySelector(".bundle-builder__selected-products");
    var slots = [].slice.call(
      block.querySelectorAll(".bundle-builder__selected-product[data-index]"),
    );
    // First paint: set the right slot count with NO animation/tween so the page never shows the
    // full (8-slot) summary then visibly shrink to the active tier on load.
    if (instant) {
      slots.forEach(function (slot) {
        var idx = parseInt(slot.getAttribute("data-index"), 10);
        slot.classList.remove("lsg-slot-enter", "lsg-slot-leave");
        slot.style.display = idx < max ? "" : "none";
      });
      return;
    }
    var toShow = [],
      toHide = [];
    slots.forEach(function (slot) {
      var idx = parseInt(slot.getAttribute("data-index"), 10);
      var shouldShow = idx < max;
      var isHidden = slot.style.display === "none";
      if (shouldShow && isHidden) toShow.push(slot);
      else if (!shouldShow && !isHidden) toHide.push(slot);
    });
    if (!toShow.length && !toHide.length) return;

    // Measure container height before/after the row-count change so we can tween between them.
    // (transform/opacity don't affect offsetHeight, so the entering slots' enter-state is safe.)
    var startH = container ? container.offsetHeight : 0;
    toShow.forEach(function (slot) {
      slot.style.display = "";
      slot.classList.add("lsg-slot-enter");
    });
    toHide.forEach(function (slot) {
      slot.style.display = "none";
    });
    var endH = container ? container.offsetHeight : 0;
    // Put the leaving slots back in flow (visible) so they can animate out in place.
    toHide.forEach(function (slot) {
      slot.style.display = "";
    });

    if (container && startH !== endH) {
      container.style.overflow = "hidden";
      container.style.height = startH + "px";
      container.style.transition =
        "height 0.35s cubic-bezier(0.19,1,0.22,1)";
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          container.style.height = endH + "px";
        });
      });
      setTimeout(function () {
        container.style.height = "";
        container.style.overflow = "";
        container.style.transition = "";
      }, 400);
    }

    // Animate the slots (fade + scale) in sync with the height tween.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        toShow.forEach(function (slot) {
          slot.classList.remove("lsg-slot-enter");
        });
        toHide.forEach(function (slot) {
          slot.classList.add("lsg-slot-leave");
        });
      });
    });
    setTimeout(function () {
      toHide.forEach(function (slot) {
        slot.style.display = "none";
        slot.classList.remove("lsg-slot-leave");
      });
    }, 350);
  }

  function activate(block, btn, tiers, instant) {
    tiers.forEach(function (t) {
      t.classList.remove("lsg-bundle-size-select-el--selected");
    });
    btn.classList.add("lsg-bundle-size-select-el--selected");
    block._lsgActiveTier = btn;
    copyTierData(block, btn);
    // Point the ATC form's variant at the active tier so the cart's parent line is that tier product
    // (it carries the matching Stay AI plan — a foreign tier/parent variant 422s "cannot apply plan").
    if (btn.dataset.variantId) {
      var atcForm = block.querySelector(".lsg-bundle-form");
      var idInput = atcForm && atcForm.querySelector('input[name="id"]');
      if (idInput) idInput.value = btn.dataset.variantId;
    }
    if (typeof window.syncSharedPickerMax === "function") {
      window.syncSharedPickerMax(block);
    }
    // Re-fit the existing flavor selection to the new tier's max: trims the surplus when going DOWN
    // (keeps the first N that fit) and re-enables the + buttons when going UP, and re-syncs the
    // per-flavor card counts + slot tiles + price. Without this the +/- max rule stays stuck at the
    // previous tier (can't add after going up; surplus flavors linger after going down).
    if (typeof window.refitBundleSelection === "function") {
      window.refitBundleSelection(block);
    } else if (typeof window.updateBundlePrice === "function") {
      window.updateBundlePrice(block);
    }
    reconcileSellingPlan(block, btn);
    // Keep the sticky bar's duplicate frequency select(s) showing the same value as the main column.
    var mainFreq = block.querySelector(
      ".lsg-bundle-interval-frequency-select:not(.is-duplicate)",
    );
    if (mainFreq) {
      block
        .querySelectorAll(".lsg-bundle-interval-frequency-select.is-duplicate")
        .forEach(function (d) {
          if (d.value !== mainFreq.value) d.value = mainFreq.value;
        });
    }
    syncSummarySlots(block, btn, instant);
    // Re-evaluate the "bundle complete" state for the new tier's min/max so the frequency selector
    // re-hides when switching to a bigger tier until the new quantity is reached.
    if (typeof window.checkoutEnableValidation === "function") {
      window.checkoutEnableValidation(block);
    }
  }

  function init() {
    var chooser = document.querySelector("[data-lsg-tier-chooser]");
    if (!chooser) return; // not a product-information bundle page

    // The buybox = the product-details column (holds tier-chooser, interval, ATC).
    var container =
      chooser.closest(".product-details") ||
      chooser.closest("[class*='product-details']") ||
      chooser.parentElement;
    if (!container) return;

    // The wrapper must span BOTH columns so the engine's getSharedPicker finds the flavor
    // picker even when it lives in the media column (not inside product-details). Use the
    // product-information section element as the wrapper.
    var wrapper =
      container.closest("[id^='shopify-section']") ||
      container.parentElement ||
      container;
    wrapper.classList.add("lsg-bundle-wrapper");

    container.classList.add("lsg-bundle-block", "lsg-bundle-block-active");
    if (!container.dataset.bundleIndex) container.dataset.bundleIndex = "1";

    var tiers = [].slice.call(chooser.querySelectorAll("[data-lsg-tier]"));
    if (!tiers.length) return;

    var first =
      tiers.filter(function (t) {
        return t.classList.contains("lsg-bundle-size-select-el--selected");
      })[0] || tiers[0];

    // Seed the block with the initial tier BEFORE the engine inits, so first price is right.
    copyTierData(container, first);

    // Hide the summary's surplus slots IMMEDIATELY (instant, no animation) — before the heavier
    // initializeBundle runs — so the page never flashes the full 8-slot summary then shrinks on load.
    syncSummarySlots(container, first, true);

    tiers.forEach(function (t) {
      if (t.dataset.lsgPiBound) return;
      t.dataset.lsgPiBound = "1";
      t.addEventListener("click", function () {
        activate(container, t, tiers);
      });
    });

    // After the engine handles a frequency / sub-otp change, re-point the selling plan to the
    // active tier's (the engine sets it from the source product's frequency-select, which is the
    // wrong tier). Delegated on the container so it survives re-renders.
    if (!container.dataset.lsgPiReconcileBound) {
      container.dataset.lsgPiReconcileBound = "1";
      container.addEventListener("change", function (e) {
        if (!e.target.closest) return;
        var freq = e.target.closest(".lsg-bundle-interval-frequency-select");
        var interval = e.target.closest(".lsg-bundle-interval-select");
        if (!freq && !interval) return;
        setTimeout(function () {
          // The sticky bar duplicates the frequency <select> (.is-duplicate). The engine only reads
          // the main one and addToCart reads the main form's selling_plan, so keep every select in
          // lockstep (they share the source product's plan options in the same order), refresh the
          // price, then re-point the main form's plan to the ACTIVE tier.
          if (freq) {
            var val = freq.value;
            container
              .querySelectorAll(".lsg-bundle-interval-frequency-select")
              .forEach(function (sel) {
                if (sel.value !== val) sel.value = val;
              });
            if (typeof window.updateBundlePrice === "function") {
              window.updateBundlePrice(container);
            }
          }
          reconcileSellingPlan(container, container._lsgActiveTier);
        }, 0);
      });
    }

    // NOTE: the frequency selector's collapse/expand is now PURE CSS — `.lsg-interval-block` starts
    // collapsed and opens via `.bundle-checkout-enabled .lsg-interval-block` (see lsg-bundle.css).
    // The engine toggles that class on the block (flavor select) and the adapter re-runs
    // checkoutEnableValidation on tier change, so no JS observer is needed — and the initial paint is
    // already collapsed (no flash of an open picker on load).

    if (typeof window.initializeBundle === "function") {
      window.initializeBundle();
    }
    activate(container, first, tiers, true);

    // Enable transitions only AFTER the initial state has painted and the engine's init churn has
    // settled — so a brief enable→disable during init doesn't play an open→close flash of the
    // frequency picker on load. Before this, the collapsed/open state snaps (see .lsg-pi-ready CSS).
    setTimeout(function () {
      container.classList.add("lsg-pi-ready");
    }, 250);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  // Re-run after full load too (assets/blocks may stream in late, mirrors lsg-bundle.js).
  window.addEventListener("load", init, { once: true });
  // Re-run when the Theme Editor re-renders the bundle's section (fresh DOM needs re-marking).
  document.addEventListener("shopify:section:load", function (e) {
    if (
      e.target &&
      e.target.querySelector &&
      e.target.querySelector("[data-lsg-tier-chooser]")
    ) {
      init();
    }
  });
})();
